import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import GenderSelection from '../../components/customLayout/GenderSelection';
import axiosInstance from '../../axiosConfig';
import { useGlobalStore } from '../../store/globalStore';
import { useClubStore } from '../../store/clubStore';
import { useTournamentStore } from '../../store/tournamentStore';
import { useDialogStore } from '../../store/dialogStore';
import doneIcon from '../../assets/images/done.svg';

interface Player {
  firstname: string;
  lastname: string;
  gender: 'M' | 'W' | '' | 'N';
  persid: string;
}

interface Props {
  maxPlayers: number;
  tournamentId: number;
  activeGender: 'MW' | 'M' | 'W' | 'N';
}

const AddNewPlayer: React.FC<Props> = ({
  maxPlayers = 1,
  activeGender,
  tournamentId,
}) => {
  const setLoading = useGlobalStore((state) => state.setLoading);
  const isLoading = useGlobalStore((state) => state.isLoading);
  const myClub = useClubStore((state) => state.myClub);
  const openDialog = useDialogStore((state) => state.openDialog);
  const activePlayers = useTournamentStore((state) => state.activePlayers);
  const setActivePlayers = useTournamentStore(
    (state) => state.setActivePlayers,
  );

  const [players, setPlayers] = useState<Player[]>(
    Array(1).fill({
      firstname: '',
      lastname: '',
      gender: activeGender === 'MW' ? '' : activeGender,
      persid: '',
    }),
  );

  const handleInputChange = (
    index: number,
    field: keyof Player,
    value: string,
  ) => {
    setPlayers((prevPlayers) =>
      prevPlayers.map((player, i) =>
        i === index ? { ...player, [field]: value } : player,
      ),
    );
  };

  const handleGenderChange = (index: number, gender: 'M' | 'W') => {
    setPlayers((prevPlayers) =>
      prevPlayers.map((player, i) =>
        i === index ? { ...player, gender } : player,
      ),
    );
  };

  const handleAddPlayer = () => {
    if (players.length < maxPlayers) {
      setPlayers((prevPlayers) => [
        ...prevPlayers,
        {
          firstname: '',
          lastname: '',
          gender: activeGender !== 'MW' ? activeGender : '',
          persid: '',
        },
      ]);
    }
  };

  const handleRemovePlayer = (index: number) => {
    setPlayers((prevPlayers) => prevPlayers.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `/tournament/${tournamentId}/players`,
        {
          club_number_country: myClub?.club_number + '',
          players,
        },
      );

      if (response.data.result.length === 0) return;

      openDialog(
        <div className="grid gap-2 py-5">
          <img className="mx-auto" src={doneIcon} alt="" />
          <div className="font-bold">🏌️ Liste der hinzugefügten Spieler :</div>
          {response.data.result.map(
            (
              player: {
                firstName: string;
                lastName: string;
                dgvmglnr: string;
                hcp: string;
              },
              index: number,
            ) => (
              <div className="border-b border-gray-100 pb-1 mb-1" key={index}>
                <div className="flex items-center mr-10 justify-between">
                  <div>
                    {' '}
                    ✅ <span className="font-medium">
                      {player.lastName}
                    </span>, {player.firstName}
                  </div>
                  <div>{player.dgvmglnr}</div>
                </div>
                {player.hcp === '' && (
                  <div>
                    <small className="text-gray-600">
                      ↳ Bitte beachten Sie, dass dieser Spieler nicht bestätigt
                      ist.
                    </small>
                  </div>
                )}
              </div>
            ),
          )}
        </div>,
        () => {},
        true,
      );

      setActivePlayers([
        ...activePlayers,
        ...response.data.result
          .filter(
            (player: { dgvmglnr: string }) =>
              !activePlayers.some(
                (activePlayer) => activePlayer.memberNumber === player.dgvmglnr,
              ),
          )
          .map((player: { firstName: string; dgvmglnr: string }) => ({
            ...player,
            color: '',
            name: player.firstName,
            memberNumber: player.dgvmglnr,
          })),
      ]);

      setPlayers(
        Array(1).fill({
          firstname: '',
          lastname: '',
          gender: activeGender === 'MW' ? '' : activeGender,
          persid: '',
        }),
      );
    } catch (error) {
      console.error('Error logging in:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border bg-white sm:border-gray-500 p-4 mx-0.5 sm:shadow-lg rounded-md">
      <div className="flex items-center justify-between">
        <div className="font-medium text-lg mb-4">Neuen Spieler hinzufügen</div>
        {players.length < maxPlayers && (
          <Button
            disabled={isLoading}
            className="!bg-green-600 hover:!bg-green-700"
            onClick={handleAddPlayer}
          >
            + Spieler
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader className="hidden lg:contents">
            <TableRow>
              <TableHead className="hidden md:inline-block">Spieler</TableHead>
              <TableHead>Vorname</TableHead>
              <TableHead>Nachname</TableHead>
              <TableHead>Geschlecht</TableHead>
              <TableHead>Mitgliedsnr</TableHead>
              <TableHead>⚙️ Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {players.map((player, index) => (
              <TableRow
                key={index}
                className="flex flex-wrap sm:table-row mb-4 pb-4 sm:mb-0 sm:pb-0"
              >
                <TableCell>
                  <div className="flex gap-1 sm:w-[90px] text-xs w-[220px]">
                    {player.gender === 'M'
                      ? '👨'
                      : player.gender === 'W'
                        ? '👩'
                        : '🏌️'}{' '}
                    Spieler #{index + 1}
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    value={player.firstname}
                    placeholder="Vorname"
                    onChange={(e) =>
                      handleInputChange(index, 'firstname', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={player.lastname}
                    placeholder="Nachname"
                    onChange={(e) =>
                      handleInputChange(index, 'lastname', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <div className="w-[220px] sm:w-full">
                    <GenderSelection
                      currentGender={player.gender}
                      onChange={(gender) => {
                        if (
                          ['MW', 'N'].includes(activeGender) ||
                          activeGender === gender
                        ) {
                          handleGenderChange(index, gender);
                        }
                      }}
                      disabled={!['MW', 'N'].includes(activeGender)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    value={player.persid}
                    placeholder="Mitgliedsnr."
                    maxLength={13}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange(index, 'persid', value);
                    }}
                  />
                </TableCell>
                <TableCell>
                  {index === 0 ? (
                    '-'
                  ) : (
                    <div
                      className="text-red-500 cursor-pointer"
                      onClick={() => handleRemovePlayer(index)}
                    >
                      🗑️
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mx-2 mt-4">
        <Button
          disabled={
            isLoading ||
            players.some(
              (player) =>
                player.firstname.length === 0 ||
                player.lastname.length === 0 ||
                player.persid.length === 0,
            )
          }
          onClick={handleSubmit}
        >
          {isLoading ? 'Lade...' : 'Spieler melden'}
        </Button>
      </div>
    </div>
  );
};

export default AddNewPlayer;
